import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  GetAllCalorificPowerAction,
  AddCalorificPowerAction,
  UpdatedCalorificPowerAction,
  DeleteCalorificPowerAction,
} from './calorific-power.actions';
import { IPoderCalorifico } from '../../core/interfaces/calorific-power.interface';
import { HttpService } from '../../core/services/http/http.service';
import { MatDialog } from '@angular/material/dialog';
import { EnovaAlertsComponent } from '../../core/utils/enova-alerts/enova-alerts.component';
import { CalorificPowerService } from '../../core/services/calorific-power/calorific-power.service';

export interface CalorificPowerStateModel {
  calorificPower: IPoderCalorifico[];
}

@State<CalorificPowerStateModel>({
  name: 'calorificPower',
  defaults: {
    calorificPower: [],
  },
})
@Injectable()
export class CalorificPowerState {
  @Selector()
  static GetAllData(state: CalorificPowerStateModel): IPoderCalorifico[] {
    return state?.calorificPower ? state?.calorificPower : [];
  }

  constructor(
    private httpService: HttpService,
    private dialog: MatDialog,
    private calorificPowerService: CalorificPowerService
  ) {}

  @Action(GetAllCalorificPowerAction)
  async GetAllCalorificPowerAction(
    ctx: StateContext<CalorificPowerStateModel>
  ) {
    try {
      const resp = await this.httpService.getCalorificPower();
      ctx.patchState({
        calorificPower: resp || [],
      });
    } catch (error) {
      console.error(error);
      ctx.patchState({
        calorificPower: [],
      });
    }
  }

  @Action(AddCalorificPowerAction)
  async AddCalorificPowerAction(
    ctx: StateContext<CalorificPowerStateModel>,
    { payload }: AddCalorificPowerAction
  ) {
    return this.httpService
      .postCalorificPower(payload)
      .then(() => {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El punto de poder calorífico se ha creado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        ctx.dispatch(new GetAllCalorificPowerAction());
      })
      .catch(err => {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            hiddeCancelBtn: true,
          },
        });
      });
  }

  @Action(UpdatedCalorificPowerAction)
  async UpdatedCalorificPowerAction(
    ctx: StateContext<CalorificPowerStateModel>,
    { payload }: UpdatedCalorificPowerAction
  ) {
    return this.httpService
      .updateCalorificPower(payload)
      .then(() => {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El punto de poder calorífico se ha actualizado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        ctx.dispatch(new GetAllCalorificPowerAction());
      })
      .catch(() => {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            hiddeCancelBtn: true,
          },
        });
      });
  }

  @Action(DeleteCalorificPowerAction)
  async DeleteCalorificPowerAction(
    ctx: StateContext<CalorificPowerStateModel>,
    { payload }: DeleteCalorificPowerAction
  ) {
    return this.httpService
      .deleteCalorificPower(payload)
      .then(async () => {
        // Limpiar el cache del servicio antes de recargar
        this.calorificPowerService.calorificPower = null;

        // Actualizar el estado local removiendo el elemento eliminado
        const currentState = ctx.getState();
        const updatedCalorificPower = (
          currentState.calorificPower || []
        ).filter((item: IPoderCalorifico) => item.uuid !== payload);
        ctx.patchState({
          calorificPower: updatedCalorificPower,
        });

        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El punto de poder calorífico se ha eliminado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });

        // Recargar datos del servidor para asegurar sincronización
        ctx.dispatch(new GetAllCalorificPowerAction());
      })
      .catch(() => {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            hiddeCancelBtn: true,
          },
        });
      });
  }
}
